/**
 * WHEN FOCUS LEAVES THE BAR (V3Chrome.tsx wires the DOM into this).
 *
 * Keyboard focus in the sticky bar drops the page's scroll padding
 * (V3Chrome.css): the bar never leaves the top, so a browser honoring the
 * padding scrolled the page about 400px to "reveal" a control that never
 * moved. The padding has to be back before any scroll that moves the reader
 * to an anchor, or that anchor lands under the bar. Following a link out of
 * the chrome is one such moment, and this decides which activations count.
 *
 * Pure: plain values in, so it is tested without a DOM.
 */

/** What a plain activation of a chrome link does to the reader. */
export type ChromeLinkMove = 'page' | 'anchor' | null

type Activation = {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

type Place = { origin: string; pathname: string; search: string }

type LinkParts = Place & { protocol: string; target: string; hash: string }

/**
 * 'anchor' when the link points at an anchor (its target will be scrolled to),
 * 'page' when it leaves for another page, null when it moves nobody: a
 * modified click (a new tab or window), a call or mail link, a link that opens
 * elsewhere, or a link to the page already showing. Enter on a link arrives as
 * a primary click with no modifier, so the keyboard counts the same.
 */
export function chromeLinkMove(activation: Activation, link: LinkParts, here: Place): ChromeLinkMove {
  if (activation.button !== 0) return null
  if (activation.metaKey || activation.ctrlKey || activation.shiftKey || activation.altKey) return null
  if (!/^https?:$/.test(link.protocol)) return null
  if (link.target && link.target !== '_self') return null
  if (link.hash) return 'anchor'
  const elsewhere = link.origin !== here.origin || link.pathname !== here.pathname || link.search !== here.search
  return elsewhere ? 'page' : null
}

/**
 * Whether a history step (Back, Forward, a hash change) moves the reader
 * somewhere the page will scroll to: an anchor, or another page. A step that
 * keeps the page and names no anchor (a search filter's query, a gallery's
 * photo) scrolls nothing, so focus, and an open menu, stay where they are.
 * `renderedPath` is the path the chrome last rendered, the page being left.
 */
export function historyStepMoves(here: { pathname: string; hash: string }, renderedPath: string): boolean {
  return Boolean(here.hash) || here.pathname !== renderedPath
}

/**
 * How a bar panel came to be open: a mouse resting on its group, or a press
 * on its caret (a click, a tap, Enter or Space). null is closed.
 */
export type ChromePanelOpenedBy = 'hover' | 'press' | null

/**
 * What a press on a bar caret does to its panel. A mouse on its way to the
 * caret has already hovered the panel open, so a press that simply toggled
 * shut the panel the visitor had just asked for (Muse site audit 2026-10-20,
 * W4). A press on a hover-opened panel holds it open; a press on a panel a
 * press opened closes it; a press on a closed panel opens it.
 */
export function chromeCaretPress(openedBy: ChromePanelOpenedBy): ChromePanelOpenedBy {
  return openedBy === 'press' ? null : 'press'
}

/**
 * Where an arrow key sends focus in a bar panel (QA walk on PR #758, W4).
 * `from` is the index of the focused panel link, or -1 when focus is on the
 * caret. From the caret, ArrowDown goes to the first link and ArrowUp to the
 * last, opening the panel first if it is closed; inside the panel both wrap,
 * and Home and End jump to the ends. Home and End on the caret stay the
 * browser's. null means the key is not the menu's to handle.
 */
export function chromeMenuStep(key: string, from: number, count: number): number | null {
  if (count <= 0) return null
  switch (key) {
    case 'ArrowDown':
      return from < 0 ? 0 : (from + 1) % count
    case 'ArrowUp':
      return from < 0 ? count - 1 : (from - 1 + count) % count
    case 'Home':
      return from < 0 ? null : 0
    case 'End':
      return from < 0 ? null : count - 1
    default:
      return null
  }
}

/**
 * Whether a pointer entering a bar group is a hover. Only a mouse hovers: a
 * touch or a pen fires enter events on the way to its tap, and a panel opened
 * by those would be counted as hovered when the tap's own press lands.
 */
export function chromePointerHovers(pointerType: string): boolean {
  return pointerType === 'mouse'
}
